import React, { useEffect, useRef, useCallback, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import apm from "core/utils/apm";
import ShortyVideo from "./ShortyVideo";
import ShortyVideoContent from "./ShortyVideoContent";
import useShortyPlaybackUrl from "./hooks/useShortyPlaybackUrl";
import useShortyViewAnalytics from "./hooks/useShortyViewAnalytics";

const ShortyPlayer = React.forwardRef(function ShortyPlayer(
  {
    content,
    active,
    preload = false,
    mainVideoRef = null,
    isMainVideoMounted = false,
    onActiveVideoReady = null,
    closeRouteDialogButton = null,
    onPrevious = null,
    onNext = null,
    canGoPrevious = false,
    canGoNext = false,
  },
  ref // eslint-disable-line no-unused-vars
) {
  const innerVideoRef = useRef(null);
  const [isPlaybackStarted, setIsPlaybackStarted] = useState(false);

  const needsPlayback = active || preload;

  const { playbackData, loading, error } = useShortyPlaybackUrl({
    content,
    enabled: needsPlayback,
  });

  useShortyViewAnalytics({
    content,
    active,
    viewId: active ? playbackData?.viewId : null,
  });

  const handleVideoReady = useCallback(() => {}, []);

  const handlePlaybackStart = useCallback(() => {
    setIsPlaybackStarted(true);
  }, []);

  const handleVideoError = useCallback((err) => {
    if (err) {
      apm.captureError(err);
    }
  }, []);

  useEffect(() => {
    if (error) {
      apm.captureError(error);
    }
  }, [error]);

  useEffect(() => {
    if (!playbackData?.playbackUrl || !active || !onActiveVideoReady) {
      return;
    }

    onActiveVideoReady({
      playbackUrl: playbackData.playbackUrl,
      videoId: playbackData.videoId,
      viewId: playbackData.viewId,
      content: playbackData.content,
      drm: playbackData.drm,
    });
  }, [playbackData, active, onActiveVideoReady]);

  useEffect(() => {
    if (!active) {
      setIsPlaybackStarted(false);
      return;
    }

    setIsPlaybackStarted(false);
  }, [active, content?.id, playbackData?.playbackUrl]);

  if (!content) {
    return null;
  }

  if (!playbackData?.playbackUrl) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundImage: content.previewUrl
            ? `url(${content.previewUrl})`
            : "none",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundColor: "#000",
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: active
              ? "rgba(0, 0, 0, 0.1)"
              : "rgba(0, 0, 0, 0.3)",
            zIndex: 1,
          }}
        />

        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            textAlign: "center",
            color: "white",
          }}
        >
          {loading && <CircularProgress color="inherit" />}
        </Box>
      </Box>
    );
  }

  return (
    <ShortyVideo
      playbackUrl={playbackData.playbackUrl}
      innerVideoRef={innerVideoRef}
      mainVideoRef={active ? mainVideoRef : null}
      isMainVideoMounted={active ? isMainVideoMounted : false}
      onReady={handleVideoReady}
      onPlaybackStart={handlePlaybackStart}
      onError={handleVideoError}
    >
      {(videoRef) => (
        <ShortyVideoContent
          innerVideoRef={videoRef}
          content={content}
          closeRouteDialogButton={closeRouteDialogButton}
          showThumbnail={!active || preload || !isPlaybackStarted}
          onPrevious={onPrevious}
          onNext={onNext}
          canGoPrevious={canGoPrevious}
          canGoNext={canGoNext}
          preload={preload}
          active={active}
        />
      )}
    </ShortyVideo>
  );
});

export default ShortyPlayer;

ShortyPlayer.propTypes = {
  content: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    previewUrl: PropTypes.string,
    premium: PropTypes.bool,
    access: PropTypes.shape({
      token: PropTypes.string,
    }),
    getIpUrl: PropTypes.string,
  }).isRequired,
  active: PropTypes.bool.isRequired,
  preload: PropTypes.bool,
  mainVideoRef: PropTypes.object,
  isMainVideoMounted: PropTypes.bool,
  onActiveVideoReady: PropTypes.func,
  closeRouteDialogButton: PropTypes.node,
  onPrevious: PropTypes.func,
  onNext: PropTypes.func,
  canGoPrevious: PropTypes.bool,
  canGoNext: PropTypes.bool,
};
